import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, FormControl, FormLabel, FormControlLabel, Typography, Box, makeStyles } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import HistoryIcon from '@mui/icons-material/History';
import store from '../../../redux/store';

const styles = makeStyles(theme => ({
  root: {
    display: 'flex',
  },
  formWrapper: {
    position: 'relative',
    width: '100%',
  },
  formControl: {
    margin: '20px 0',
    marginBottom: 0,
  },
  checkbox: {
    pointerEvents: 'auto',
    '&:not(.Mui-disabled)': {
      color: '#9C27B0 !important',
    },
    '&.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.26) !important',
    },
  },
  labelControl: {
    pointerEvents: 'none',
    '& .MuiFormLabel-root': {
      color: '#000000',
    },
  },
  labelControlDark: {
    pointerEvents: 'none',
    '& .MuiFormLabel-root': {
      color: '#BEBEBE',
    },
  },
  group: {
    margin: 0,
    display: 'flex',
    flexDirection: 'row',
  },
  labelMinus: {
    paddingLeft: '9px',
  },
  labelPlus: {
    paddingLeft: '5px',
  },
  title: {
    width: '48px',
  },
  disabledOverlay: {
    position: 'absolute',
    top: 10,
    left: -10,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.type === 'light' ? '#CCD6E1B2' : '#1C1C1CB2',
    opacity: 1,
    zIndex: 10,
    width: '80%',
    cursor: 'not-allowed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabledText: {
    color: theme.palette.type === 'light' ? '#042440' : '#BEBEBE',
    fontWeight: 'bold',
    fontSize: '15px',
  },
  disabledIcon: {
    color: theme.palette.type === 'light' ? '#5083CF' : '#BC75BC',
    marginRight: '5px',
    marginLeft: '10px',
    fontSize: '24px',
  },
}));

const MarketPlaceSelection = props => {
  const theme = useTheme();
  const { marketplaces, pricingGroupID, includeExclude, inclusions, setInclusions, exclusions, setExclusions } = props;
  const [allSelected, setAll] = useState(
    includeExclude.inclusions.length === 0 && includeExclude.exclusions.length === 0
  );
  // eslint-disable-next-line prefer-destructuring
  const selectedGroup = store.getState().pricing.selectedGroup;
  const { internal_notes_validation: internalNotesValidation } = selectedGroup;
  const disabled = internalNotesValidation === 'PENDING';
  const classes = styles();
  const handleSelectAll = (_, checked) => {
    if (checked) {
      setExclusions([]);
      setInclusions([]);
    } else {
      setInclusions([]);
      const excludeAll = [];
      Object.keys(marketplaces).forEach(m => {
        excludeAll.push(m);
      });
      setExclusions(excludeAll);
    }
    setAll(!allSelected);
  };

  const handleSelect = (event, checked) => {
    const { value } = event.target;
    const name = value.split('_')[0];
    if (!checked) {
      const index = inclusions.findIndex(i => i === name);
      setAll(false);
      const exclusionsCopy = [...exclusions];
      exclusionsCopy.push(name);
      setExclusions(exclusionsCopy);
      if (index > -1) {
        const inclusionsCopy = [...inclusions];
        inclusionsCopy.splice(index, 1);
        setInclusions(inclusionsCopy);
      }
      if (Object.keys(marketplaces).length - exclusionsCopy.length === 1) {
        Object.keys(marketplaces).forEach(k => {
          if (!exclusionsCopy.includes(k)) {
            setInclusions([k]);
          }
        });
      }
    } else {
      const index = exclusions.findIndex(i => i === name);
      if (index > -1) {
        const exclusionsCopy = [...exclusions];
        exclusionsCopy.splice(index, 1);
        setExclusions(exclusionsCopy);
        setAll(exclusionsCopy.length === 0 && inclusions.length === 0);
      }
      if (inclusions.length === 0 && exclusions.length === Object.keys(marketplaces).length) {
        const inclusionsCopy = [...inclusions];
        inclusionsCopy.push(name);
        setInclusions(inclusionsCopy);
      } else {
        if (inclusions.length > 0) {
          const exclusionsCopy = [];
          Object.keys(marketplaces).forEach(m => {
            if (!(inclusions.includes(m) || m === name)) {
              exclusionsCopy.push(m);
            }
          });
          setExclusions(exclusionsCopy);
        }
        setInclusions([]);
      }
    }
  };

  const isChecked = place => {
    const { name } = marketplaces[place];
    if (allSelected) return true;
    if (inclusions.length > 0 && inclusions.includes(name)) return true;
    if (exclusions.length > 0 && !exclusions.includes(name)) return true;
    return false;
  };

  return (
    <div className={classes.formWrapper}>
      <div className={classes.root} style={{ filter: disabled ? 'blur(2px)' : 'none' }}>
        <FormControl component="fieldset" className={classes.formControl}>
          <FormControlLabel
            value="ALL"
            className={theme.palette.type === 'light' ? classes.labelControl : classes.labelControlDark}
            key={`ALL - ${pricingGroupID}`}
            control={
              <Checkbox
                className={classes.checkbox}
                checked={allSelected}
                onChange={handleSelectAll}
                disabled={disabled}
              />
            }
            label={<FormLabel focused={false}>(All)</FormLabel>}
          />
          {Object.keys(marketplaces).map(place => (
            <FormControlLabel
              className={theme.palette.type === 'light' ? classes.labelControl : classes.labelControlDark}
              key={`${marketplaces[place].name} - ${pricingGroupID}`}
              value={`${marketplaces[place].name}_${marketplaces[place].name}`}
              control={
                <Checkbox
                  checked={isChecked(place)}
                  className={classes.checkbox}
                  onChange={handleSelect}
                  disabled={disabled}
                />
              }
              label={<FormLabel focused={false}>{marketplaces[place].name}</FormLabel>}
            />
          ))}
        </FormControl>
      </div>
      {disabled && (
        <Box className={classes.disabledOverlay}>
          <HistoryIcon className={classes.disabledIcon} />
          <Typography className={classes.disabledText}>
            Preparing internal notes before setting broadcasting rules
          </Typography>
        </Box>
      )}
    </div>
  );
};

MarketPlaceSelection.propTypes = {
  marketplaces: PropTypes.object.isRequired,
  pricingGroupID: PropTypes.number.isRequired,
  includeExclude: PropTypes.object.isRequired,
  inclusions: PropTypes.array.isRequired,
  setInclusions: PropTypes.func.isRequired,
  exclusions: PropTypes.array.isRequired,
  setExclusions: PropTypes.func.isRequired,
};

export default MarketPlaceSelection;
